import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Modal,
  Box,
  Typography,
} from "@mui/material";
import axios from "axios";
import 'animate.css';

const apiUrl = process.env.REACT_APP_API_RESERVAS;

const InformesFederaciones = () => {
  const [federacion, setFederacion] = useState("");
  const [federacionesDisponibles, setFederacionesDisponibles] = useState([]);
  const [results, setResults] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalDays, setTotalDays] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    const cargarFederaciones = async () => {
      try {
        const response = await axios.get(apiUrl);
        const allReservations = response.data;
  
        const federaciones = Array.from(
          new Set(
            allReservations.flatMap(res =>
              res.miembros.map(m => m.federacion_deporte?.trim()).filter(Boolean)
            )
          )
        ).sort();
  
        setFederacionesDisponibles(federaciones);
      } catch (error) {
        console.error("Error al cargar federaciones:", error);
      }
    };
  
    cargarFederaciones();
  }, []);

  const calculateTotalDays = (reservations) => {
    return reservations.reduce((total, reservation) => {
      const [start, end] = reservation.days;
      const startDate = new Date(start);
      const endDate = new Date(end);
      const diffTime = Math.abs(endDate - startDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return total + diffDays;
    }, 0);
  };

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    setHasSearched(false);
    try {
      const response = await axios.get(apiUrl);
      const allReservations = response.data;

      // 🔄 Extraer federaciones únicas
      const federaciones = Array.from(
        new Set(
          allReservations.flatMap(res =>
            res.miembros.map(m => m.federacion_deporte?.trim()).filter(Boolean)
          )
        )
      ).sort();

      setFederacionesDisponibles(federaciones);

      // 🔍 Filtrar reservas por federación
      const filteredResults = allReservations.filter((reservation) =>
        reservation.miembros.some(
          (miembro) => miembro.federacion_deporte?.toLowerCase() === federacion.toLowerCase()
        )
      );

      const reservationsMapped = filteredResults.map((reservation) => ({
        id: reservation.id,
        days: [reservation.fecha_entrada, reservation.fecha_salida],
        members: reservation.miembros,
      }));

      setResults(reservationsMapped);
      setTotalDays(calculateTotalDays(reservationsMapped));
      setHasSearched(true);
    } catch (err) {
      setError("No se pudieron obtener las reservas. Intente nuevamente.");
      setResults([]);
      setTotalDays(0);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (reservation) => {
    setSelectedReservation(reservation);
  };

  const handleCloseModal = () => {
    setSelectedReservation(null);
  };

  return (
    <div style={{ padding: "20px" }}>
      <Typography variant="h5" gutterBottom>
        Informe de Reservas por Federación
      </Typography>

      <div style={{ display: "flex", gap: "10px", marginBottom: "20px", maxWidth: 500 }}>
        <Autocomplete
          options={federacionesDisponibles}
          value={federacion}
          onChange={(event, newValue) => setFederacion(newValue || "")}
          renderInput={(params) => (
            <TextField {...params} label="Seleccione Federación" variant="outlined" />
          )}
          fullWidth
        />
        <Button variant="contained" onClick={handleSearch} disabled={loading || !federacion}>
          {loading ? "Buscando..." : "Buscar"}
        </Button>
      </div>

      {error && <Typography color="error">{error}</Typography>}

      {results.length > 0 ? (
        <>
          <Typography variant="h6" gutterBottom>
            Total de días reservados: {totalDays}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>N° de Reserva</TableCell>
                  <TableCell>Días Reservados</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((reservation) => (
                  <TableRow
                    key={reservation.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleOpenModal(reservation)}
                  >
                    <TableCell>{reservation.id}</TableCell>
                    <TableCell>{reservation.days.join(" - ")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        !loading &&
        hasSearched &&
        results.length === 0 && (
          <Typography>No se encontraron reservas para esta federación.</Typography>
        )
      )}

      <Modal open={!!selectedReservation} onClose={handleCloseModal}>
        <Box
          className={`animate__animated animate__fadeInDown`}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            minWidth: "400px",
            maxWidth: "90vw",
            textAlign: "center",
          }}
        >
          {selectedReservation && (
            <>
              <Typography variant="h6" gutterBottom>
                Detalles de la Reserva
              </Typography>
              <Typography>
                <strong>Número de Reserva:</strong> {selectedReservation.id}
              </Typography>
              <Typography>
                <strong>Días Reservados:</strong> {selectedReservation.days.join(" - ")}
              </Typography>
              <Typography variant="h6" gutterBottom>
                <strong>Personas en esta reserva:</strong>
              </Typography>
              {selectedReservation.members.map((member) => (
                <Typography key={member.id}>
                  {member.dni} - {member.nombre} - {member.apellido} - {member.federacion_deporte}
                </Typography>
              ))}
            </>
          )}
          <Button
            variant="contained"
            color="success"
            style={{ marginTop: "20px" }}
            onClick={handleCloseModal}
          >
            Cerrar
          </Button>
        </Box>
      </Modal>
    </div>
  );
};

export default InformesFederaciones;